import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Radio,
  RadioGroup,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Textarea,
  tokens,
} from '@fluentui/react-components';
import { DismissRegular, SearchRegular } from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { PageHeader } from '../components/PageHeader';
import { formatDate, formatDateTime, formatValidity, stateColor, toDateInput } from '../features/guests/guest-format';
import '../features/guests/guests.css';
import type { ActionResponse, GuestSummaryDto } from '../api/types';

export function GuestsEditPage() {
  const api = useApi();
  const guests = useAsync(() => api.listGuests(), [api]);
  const policies = useAsync(() => api.getPagePolicies(), [api]);
  const editTicketMode = policies.data?.find((p) => p.pageKey === 'guests.edit')?.ticketMode ?? 'None';
  const revokePolicy = policies.data?.find((p) => p.pageKey === 'guests.revoke');
  const revokeTicketMode = revokePolicy?.ticketMode ?? 'None';
  const revokeNeedsApproval = revokePolicy?.approvalMode === 'Required';

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<GuestSummaryDto | null>(null);
  const [unlimited, setUnlimited] = useState(false);
  const [validUntil, setValidUntil] = useState('');
  const [editTicket, setEditTicket] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ActionResponse | null>(null);

  const [revokeOpen, setRevokeOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [revokeMode, setRevokeMode] = useState<'block' | 'delete'>('block');
  const [revokeTicket, setRevokeTicket] = useState('');

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = guests.data?.guests ?? [];
    const filtered = q
      ? all.filter(
          (g) =>
            g.displayName.toLowerCase().includes(q) ||
            (g.company ?? '').toLowerCase().includes(q) ||
            g.mail.toLowerCase().includes(q),
        )
      : all;
    return filtered.slice(0, 50);
  }, [guests.data, search]);

  /** Auswahl übernehmen und das Formular mit der bestehenden Gültigkeit vorbelegen. */
  function select(guest: GuestSummaryDto) {
    setSelected(guest);
    setUnlimited(!guest.validUntilUtc);
    setValidUntil(toDateInput(guest.validUntilUtc));
    setEditTicket('');
    setResult(null);
    setError(null);
  }

  async function saveValidity() {
    if (!selected) return;
    if (editTicketMode === 'Required' && !editTicket.trim()) {
      setError('Für diese Aktion ist eine Ticketnummer erforderlich (Governance).');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.updateGuest(selected.id, {
        validUntil: unlimited ? undefined : validUntil || undefined,
        ticketNumber: editTicket.trim() || undefined,
      });
      setResult(res);
      guests.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    if (!selected) return;
    if (!reason.trim()) {
      setError('Für den Widerruf ist ein Grund erforderlich.');
      return;
    }
    if (revokeTicketMode === 'Required' && !revokeTicket.trim()) {
      setError('Für diese Aktion ist eine Ticketnummer erforderlich (Governance).');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.revokeGuest(selected.id, {
        reason: reason.trim(),
        delete: revokeMode === 'delete',
        ticketNumber: revokeTicket.trim() || undefined,
      });
      setResult(res);
      setRevokeOpen(false);
      setReason('');
      setRevokeTicket('');
      // Nach dem Löschen ist der Gast weg — Auswahl aufheben.
      if (revokeMode === 'delete') setSelected(null);
      guests.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Gast ändern"
        description="Gültigkeit einer Gast-Einladung anpassen oder die Einladung widerrufen."
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, maxWidth: 900 }}>
        {error ? (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        ) : null}

        {result ? (
          <MessageBar intent={result.status === 'PendingApproval' ? 'info' : 'success'}>
            <MessageBarBody>
              <MessageBarTitle>{result.status === 'PendingApproval' ? 'Antrag eingereicht' : 'Erledigt'}</MessageBarTitle>
              {result.status === 'PendingApproval'
                ? 'Die Aktion ist freigabepflichtig. Den Status findest du unter „Meine Anfragen".'
                : result.message}
            </MessageBarBody>
          </MessageBar>
        ) : null}

        <div style={{ maxWidth: 360 }}>
          <Input
            placeholder="Gast nach Name, Firma oder Mail suchen…"
            value={search}
            onChange={(_, d) => setSearch(d.value)}
            contentBefore={<SearchRegular />}
            contentAfter={
              search ? (
                <DismissRegular style={{ cursor: 'pointer' }} onClick={() => setSearch('')} aria-label="Suche leeren" />
              ) : undefined
            }
          />
        </div>

        {guests.loading ? <Spinner label="Lade Gast-Einladungen…" /> : null}
        {guests.error ? (
          <MessageBar intent="error">
            <MessageBarBody>{guests.error.message}</MessageBarBody>
          </MessageBar>
        ) : null}

        {guests.data ? (
          rows.length === 0 ? (
            <Text>Keine Gast-Einladungen gefunden.</Text>
          ) : (
            <div className="guest-table-wrap">
              <Table size="small" aria-label="Gast auswählen" className="guest-table">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell style={{ width: '22%' }}>Name</TableHeaderCell>
                    <TableHeaderCell style={{ width: '18%' }}>Firma</TableHeaderCell>
                    <TableHeaderCell style={{ width: '30%' }}>Mail</TableHeaderCell>
                    <TableHeaderCell style={{ width: '18%' }}>Gültig bis</TableHeaderCell>
                    <TableHeaderCell style={{ width: '12%' }}>Status</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((g) => (
                    <TableRow
                      key={g.id}
                      className="guest-row-selectable"
                      style={{
                        backgroundColor: selected?.id === g.id ? tokens.colorNeutralBackground2Selected : undefined,
                      }}
                      onClick={() => select(g)}
                    >
                      <TableCell>
                        <span className="pii">{g.displayName}</span>
                      </TableCell>
                      <TableCell>{g.company ?? '—'}</TableCell>
                      <TableCell>
                        <span className="pii">{g.mail || '—'}</span>
                      </TableCell>
                      <TableCell style={{ fontVariantNumeric: 'tabular-nums' }}>{formatValidity(g)}</TableCell>
                      <TableCell>
                        <Badge appearance="tint" color={stateColor(g.state)}>
                          {g.state}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )
        ) : null}

        {selected ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: tokens.spacingVerticalL,
              padding: tokens.spacingVerticalL,
              borderRadius: tokens.borderRadiusLarge,
              backgroundColor: tokens.colorNeutralBackground2,
            }}
          >
            <div>
              <Text weight="semibold">
                <span className="pii">{selected.displayName}</span>
              </Text>
              <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                <span className="pii">{selected.mail}</span>
                {selected.company ? ` · ${selected.company}` : ''} · eingeladen am {formatDate(selected.invitedUtc)}
                {selected.invitedByUpn ? ` von ${selected.invitedByUpn}` : ''} · letzte Anmeldung{' '}
                {selected.signInAvailable ? formatDateTime(selected.lastSignIn) : 'n/v'}
              </Caption1>
            </div>

            <Field
              label="Gültig bis"
              hint={
                unlimited
                  ? 'Der Zugang läuft nicht automatisch ab.'
                  : 'Ein bereits wegen Ablaufs gesperrter Zugang wird beim Verlängern wieder freigegeben.'
              }
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                <Input
                  type="date"
                  value={validUntil}
                  disabled={unlimited}
                  onChange={(_, d) => setValidUntil(d.value)}
                />
                <Switch checked={unlimited} onChange={(_, d) => setUnlimited(d.checked)} label="unbefristet" />
              </div>
            </Field>

            {editTicketMode !== 'None' ? (
              <Field label="Ticketnummer" required={editTicketMode === 'Required'}>
                <Input value={editTicket} onChange={(_, d) => setEditTicket(d.value)} placeholder="z. B. REQ0012345" />
              </Field>
            ) : null}

            <div style={{ display: 'flex', gap: tokens.spacingHorizontalM }}>
              <Button appearance="primary" disabled={busy} onClick={saveValidity}>
                {busy ? 'Bitte warten…' : 'Gültigkeit speichern'}
              </Button>
              <Button
                appearance="secondary"
                disabled={busy}
                onClick={() => {
                  setError(null);
                  setRevokeOpen(true);
                }}
              >
                Einladung widerrufen
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <Dialog open={revokeOpen} onOpenChange={(_, d) => (!d.open ? setRevokeOpen(false) : undefined)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Einladung widerrufen</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                <Text>
                  Zugang von <span className="pii">{selected?.displayName}</span> widerrufen?
                </Text>

                <Field label="Art des Widerrufs">
                  <RadioGroup value={revokeMode} onChange={(_, d) => setRevokeMode(d.value as 'block' | 'delete')}>
                    <Radio value="block" label="Zugriff sperren — Konto bleibt bestehen, Anmeldung wird sofort beendet" />
                    <Radio value="delete" label="Gastkonto löschen — endgültig, Mitgliedschaften gehen verloren" />
                  </RadioGroup>
                </Field>

                <Field label="Begründung" required>
                  <Textarea
                    value={reason}
                    onChange={(_, d) => setReason(d.value)}
                    placeholder="Warum wird der Zugang widerrufen?"
                  />
                </Field>

                {revokeTicketMode !== 'None' ? (
                  <Field label="Ticketnummer" required={revokeTicketMode === 'Required'}>
                    <Input
                      value={revokeTicket}
                      onChange={(_, d) => setRevokeTicket(d.value)}
                      placeholder="z. B. INC-12345"
                    />
                  </Field>
                ) : null}

                {revokeNeedsApproval ? (
                  <MessageBar intent="info">
                    <MessageBarBody>Der Widerruf ist freigabepflichtig und wird erst nach Genehmigung ausgeführt.</MessageBarBody>
                  </MessageBar>
                ) : null}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setRevokeOpen(false)} disabled={busy}>
                Abbrechen
              </Button>
              <Button appearance="primary" onClick={revoke} disabled={busy || !reason.trim()}>
                {busy ? 'Wird verarbeitet…' : revokeMode === 'delete' ? 'Konto löschen' : 'Zugriff sperren'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
